import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, SimpleGrid, Spinner,
  Badge, Icon, Divider, Alert, AlertIcon,
} from '@chakra-ui/react';
import { FiRefreshCw, FiDatabase, FiBox, FiTag, FiKey } from 'react-icons/fi';

const API_BASE = '/api';

function HealthCheck() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchHealth = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/health/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => { setHealth(data); setLastChecked(new Date()); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const statusColor = (status) => {
    switch (status) {
      case 'healthy': case 'connected': return 'green';
      case 'degraded': return 'orange';
      case 'not_configured': return 'yellow';
      case 'error': case 'unhealthy': return 'red';
      default: return 'gray';
    }
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="cyan.500" /></Box>;
  if (!health) return <Alert status="error"><AlertIcon />Failed to load health check data</Alert>;

  const checks = health.checks || {};

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Health Check</Heading>
          <Text color="gray.500">
            Service connectivity and status
            {lastChecked && ` — last checked ${lastChecked.toLocaleTimeString()}`}
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="cyan" onClick={fetchHealth} isLoading={loading}>Re-check</Button>
      </HStack>

      <Alert
        status={health.overall === 'healthy' ? 'success' : health.overall === 'degraded' ? 'warning' : 'error'}
        borderRadius="lg" mb={6} py={4}
      >
        <AlertIcon boxSize={6} />
        <VStack align="start" spacing={0} ml={2}>
          <Text fontWeight="bold" fontSize="lg">
            Overall: <Badge colorScheme={statusColor(health.overall)} fontSize="md" px={2}>{health.overall}</Badge>
          </Text>
        </VStack>
      </Alert>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        {/* Database */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiDatabase} boxSize={6} color="blue.500" />
            <Heading size="md">Database</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.database?.status)} fontSize="sm">{checks.database?.status || 'unknown'}</Badge>
            </HStack>
            {checks.database?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.database.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.database?.message && (
              <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{checks.database.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* Applications */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiBox} boxSize={6} color="cyan.500" />
            <Heading size="md">Applications</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.applications?.status)} fontSize="sm">{checks.applications?.status || 'unknown'}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Total</Text>
              <Text fontWeight="bold">{checks.applications?.total || 0}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Active</Text>
              <Text fontWeight="bold">{checks.applications?.active || 0}</Text>
            </HStack>
            {checks.applications?.message && (
              <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{checks.applications.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* Versions */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiTag} boxSize={6} color="purple.500" />
            <Heading size="md">Versions</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.versions?.status)} fontSize="sm">{checks.versions?.status || 'unknown'}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Total</Text>
              <Text fontWeight="bold">{checks.versions?.total || 0}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Stable</Text>
              <Text fontWeight="bold">{checks.versions?.stable || 0}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Deprecated</Text>
              <Text fontWeight="bold">{checks.versions?.deprecated || 0}</Text>
            </HStack>
            {checks.versions?.message && (
              <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{checks.versions.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* API Keys */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiKey} boxSize={6} color="orange.500" />
            <Heading size="md">API Keys</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.api_keys?.status)} fontSize="sm">{checks.api_keys?.status || 'unknown'}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Total</Text>
              <Text fontWeight="bold">{checks.api_keys?.total || 0}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Active</Text>
              <Text fontWeight="bold">{checks.api_keys?.active || 0}</Text>
            </HStack>
            {checks.api_keys?.message && (
              <Alert status="warning" borderRadius="md" fontSize="sm"><AlertIcon />{checks.api_keys.message}</Alert>
            )}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export default HealthCheck;
